import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import Bugsnag from '@bugsnag/js';
import {
  EVERGREEN_HEALTHY_PLAYBACK_S,
  EVERGREEN_PROBE_GIVE_UP_MS,
  evergreenSourceUrl,
  hasEvergreenFallback,
  isEvergreenPlaybackStalled,
  otherEvergreenSource,
  resolveEvergreenCandidates,
  resolveEvergreenRaceWinner,
  resolveEvergreenRecovery,
  type EvergreenSourceKind,
} from '@/lib/evergreenVideoSource';
import { clampEvergreenPosition } from '@/lib/videoSeekGuard';

const bugsnagApiKey = import.meta.env.VITE_BUGSNAG_API_KEY;
const isBugsnagEnabled = bugsnagApiKey !== undefined && bugsnagApiKey !== '';

/** Écart toléré entre la position d'un candidat et la position visée pour le déclarer prêt. */
const RACE_POSITION_TOLERANCE_S = 2;

/** Événements après lesquels un candidat a pu devenir jouable à la position visée. */
const READINESS_EVENTS = [
  'loadedmetadata',
  'loadeddata',
  'canplay',
  'canplaythrough',
  'seeked',
  'progress',
  'playing',
] as const;

const RACE_TICK_MS = 250;
const WATCHDOG_TICK_MS = 1000;

const report = (message: string, metadata: Record<string, unknown>): void => {
  if (!isBugsnagEnabled) return;
  Bugsnag.notify(new Error(message), (event) => {
    event.severity = 'warning';
    event.addMetadata('evergreenVideo', metadata);
  });
};

type UseEvergreenVideoSourceParams = {
  enabled: boolean;
  videoRef: RefObject<HTMLVideoElement | null>;
  primaryUrl?: string | null;
  fallbackUrl?: string | null;
  /** Position à laquelle la lecture doit se trouver maintenant, en secondes. */
  getResumePosition: () => number;
  /** Doit passer par le déplacement contrôlé de la page, sinon le filet anti-avance se déclenche. */
  seekTo: (video: HTMLVideoElement, target: number) => void;
};

/**
 * Sert la source vidéo evergreen la plus rapide et récupère la lecture quand elle lâche.
 *
 * Au démarrage la source principale est attachée immédiatement, comme avant, et la copie de secours
 * est sondée en parallèle dans un élément détaché : elle ne prend la main que si elle est prête
 * avant. Le chemin nominal est donc inchangé, sans délai ni requête en double.
 *
 * En cours de lecture, une source qui n'alimente plus la vidéo est relancée, puis remplacée par
 * l'autre, puis abandonnée avec un état d'échec explicite.
 */
export function useEvergreenVideoSource({
  enabled,
  videoRef,
  primaryUrl,
  fallbackUrl,
  getResumePosition,
  seekTo,
}: UseEvergreenVideoSourceParams): { activeUrl: string | undefined; hasFailed: boolean } {
  const candidates = useMemo(
    () => resolveEvergreenCandidates(primaryUrl, fallbackUrl),
    [primaryUrl, fallbackUrl],
  );

  const [activeKind, setActiveKind] = useState<EvergreenSourceKind>('primary');
  const [hasFailed, setHasFailed] = useState(false);

  const raceSettledRef = useRef(false);
  const hasFailedRef = useRef(false);
  const triedSourcesRef = useRef<Set<EvergreenSourceKind>>(new Set());
  const reloadsRef = useRef(0);
  const playedSecondsRef = useRef(0);
  const lastProgressAtRef = useRef(0);
  const lastTimeRef = useRef(0);

  // Les callbacks de la page changent à chaque rendu : les garder dans des refs évite de réarmer
  // la course et la surveillance en boucle.
  const getResumePositionRef = useRef(getResumePosition);
  getResumePositionRef.current = getResumePosition;
  const seekToRef = useRef(seekTo);
  seekToRef.current = seekTo;

  const activeUrl = evergreenSourceUrl(candidates, activeKind) ?? undefined;

  useEffect(() => {
    raceSettledRef.current = false;
    hasFailedRef.current = false;
    triedSourcesRef.current = new Set();
    reloadsRef.current = 0;
    setActiveKind('primary');
    setHasFailed(false);
  }, [candidates]);

  useEffect(() => {
    if (!enabled || raceSettledRef.current || !hasEvergreenFallback(candidates)) return;
    const probeUrl = candidates.fallbackUrl;
    if (!videoRef.current || probeUrl === null) return;
    const video: HTMLVideoElement = videoRef.current;

    const probe = document.createElement('video');
    probe.muted = true;
    probe.preload = 'auto';
    probe.playsInline = true;
    probe.src = probeUrl;

    let probeSeekRequested = false;
    let settled = false;
    let primaryReadyAt: number | null = null;
    let fallbackReadyAt: number | null = null;
    let intervalId = 0;
    const startedAt = Date.now();

    const isReadyAtTarget = (element: HTMLVideoElement): boolean => {
      if (element.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) return false;
      const target = clampEvergreenPosition(getResumePositionRef.current(), element.duration);
      return Math.abs(element.currentTime - target) <= RACE_POSITION_TOLERANCE_S;
    };

    const teardown = () => {
      window.clearInterval(intervalId);
      for (const event of READINESS_EVENTS) {
        video.removeEventListener(event, check);
        probe.removeEventListener(event, check);
      }
      probe.removeAttribute('src');
      probe.load();
    };

    const settle = (winner: EvergreenSourceKind) => {
      if (settled) return;
      settled = true;
      raceSettledRef.current = true;
      teardown();
      if (winner === 'fallback') {
        setActiveKind('fallback');
        report('Evergreen video served from fallback source', {
          reason: 'race',
          elapsedMs: (fallbackReadyAt ?? Date.now()) - startedAt,
        });
      }
    };

    // Chaque candidat est horodaté au moment exact où il devient jouable, sur l'événement qui le
    // rend jouable : comparer deux états lus périodiquement désignerait la principale dès que les deux
    // sont prêtes dans le même intervalle, quel que soit l'ordre réel d'arrivée.
    function check() {
      if (settled) return;
      if (probe.error !== null) {
        settle('primary');
        return;
      }
      if (!probeSeekRequested && probe.readyState >= HTMLMediaElement.HAVE_METADATA) {
        probe.currentTime = clampEvergreenPosition(getResumePositionRef.current(), probe.duration);
        probeSeekRequested = true;
      }
      if (primaryReadyAt === null && isReadyAtTarget(video)) primaryReadyAt = Date.now();
      if (fallbackReadyAt === null && probeSeekRequested && isReadyAtTarget(probe)) {
        fallbackReadyAt = Date.now();
      }
      const winner = resolveEvergreenRaceWinner({
        primaryReadyAt,
        fallbackReadyAt,
        deadlineReached: Date.now() - startedAt >= EVERGREEN_PROBE_GIVE_UP_MS,
      });
      if (winner !== null) settle(winner);
    }

    for (const event of READINESS_EVENTS) {
      video.addEventListener(event, check);
      probe.addEventListener(event, check);
    }
    // Filet : porte l'échéance d'abandon et couvre un candidat qui deviendrait jouable sans
    // qu'aucun événement ne soit émis.
    intervalId = window.setInterval(check, RACE_TICK_MS);
    probe.load();
    check();

    return teardown;
    // `activeKind` démonte la course dès qu'un incident impose une source : la balise vidéo ne
    // porte alors plus la principale, la mesurer fausserait l'arbitrage et sa remontée.
  }, [enabled, candidates, videoRef, activeKind]);

  useEffect(() => {
    if (!enabled || activeUrl === undefined || !videoRef.current) return;
    const video: HTMLVideoElement = videoRef.current;

    let watchdogId = 0;

    triedSourcesRef.current.add(activeKind);
    reloadsRef.current = 0;
    playedSecondsRef.current = 0;
    lastProgressAtRef.current = Date.now();
    lastTimeRef.current = video.currentTime;

    const markProgress = () => {
      lastProgressAtRef.current = Date.now();
    };

    /**
     * La lecture est repartie alors qu'on l'avait abandonnée : l'état d'échec doit disparaître,
     * sinon le message d'indisponibilité resterait affiché par-dessus une vidéo qui joue.
     */
    const recoverFromFailure = () => {
      if (!hasFailedRef.current) return;
      hasFailedRef.current = false;
      reloadsRef.current = 0;
      markProgress();
      armWatchdog();
      setHasFailed(false);
    };

    const onTimeUpdate = () => {
      const delta = video.currentTime - lastTimeRef.current;
      lastTimeRef.current = video.currentTime;
      if (delta === 0) return;
      // Un saut plus large qu'une seconde de lecture est un recalage, pas du temps joué.
      if (delta > 0 && delta < 2) {
        playedSecondsRef.current += delta;
        if (playedSecondsRef.current >= EVERGREEN_HEALTHY_PLAYBACK_S) reloadsRef.current = 0;
        recoverFromFailure();
      }
      markProgress();
    };

    const onPlaying = () => {
      recoverFromFailure();
      markProgress();
    };

    /**
     * Le téléchargement vaut preuve de vie, sans exiger que la plage tamponnée grandisse : sur
     * connexion lente les premiers octets arrivent bien avant qu'une image soit décodable, et
     * attendre le tampon revenait à déclarer morte une source qui répondait parfaitement.
     * Le navigateur n'émet plus cet événement dès que les données cessent réellement d'arriver.
     */
    const onDownloadProgress = () => {
      markProgress();
    };

    const handleFailure = (reason: 'error' | 'stall') => {
      // L'abandon est terminal : la vidéo restant à l'arrêt, la condition de panne reste vraie et
      // sans ce verrou la surveillance rejouerait l'abandon, et sa remontée, à chaque seconde.
      if (hasFailedRef.current) return;

      const action = resolveEvergreenRecovery({
        candidates,
        activeKind,
        reason,
        reloadsOnActiveSource: reloadsRef.current,
        otherSourceAlreadyTried: triedSourcesRef.current.has(otherEvergreenSource(activeKind)),
        activeSourcePlayedHealthy: playedSecondsRef.current >= EVERGREEN_HEALTHY_PLAYBACK_S,
        hasPlayedAnything: playedSecondsRef.current > 0,
      });

      if (action === 'wait') {
        // Rien à faire, mais on repart pour un tour complet : sans ça la surveillance rejouerait
        // ce verdict à chaque seconde.
        markProgress();
        return;
      }

      if (action === 'reload') {
        reloadsRef.current += 1;
        playedSecondsRef.current = 0;
        markProgress();
        video.addEventListener(
          'loadedmetadata',
          () => {
            seekToRef.current(
              video,
              clampEvergreenPosition(getResumePositionRef.current(), video.duration),
            );
            void video.play().catch(() => {});
          },
          { once: true },
        );
        video.load();
        return;
      }

      if (action === 'switch') {
        const next = otherEvergreenSource(activeKind);
        // Après un incident la course n'a plus de sens : la source retenue est imposée.
        raceSettledRef.current = true;
        if (next === 'fallback') {
          report('Evergreen video served from fallback source', { reason });
        }
        setActiveKind(next);
        return;
      }

      hasFailedRef.current = true;
      window.clearInterval(watchdogId);
      report('Evergreen video playback gave up', {
        reason,
        source: activeKind,
        hasFallback: hasEvergreenFallback(candidates),
      });
      setHasFailed(true);
    };

    const onError = () => handleFailure('error');

    // Un onglet en arrière-plan ne télécharge plus : sans ce recalage, le retour au premier plan
    // serait immédiatement pris pour une panne.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') markProgress();
    };

    function armWatchdog() {
      window.clearInterval(watchdogId);
      watchdogId = window.setInterval(() => {
        const stalled = isEvergreenPlaybackStalled({
          documentVisible: document.visibilityState === 'visible',
          ended: video.ended,
          paused: video.paused,
          bufferedAhead: video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA,
          secondsSinceLastProgress: (Date.now() - lastProgressAtRef.current) / 1000,
        });
        if (stalled) handleFailure('stall');
      }, WATCHDOG_TICK_MS);
    }

    armWatchdog();

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('progress', onDownloadProgress);
    video.addEventListener('error', onError);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(watchdogId);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('progress', onDownloadProgress);
      video.removeEventListener('error', onError);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, activeUrl, activeKind, candidates, videoRef]);

  return { activeUrl, hasFailed };
}
